import React, { useCallback, useEffect, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiCopyButtonProps {
  text: string;
  /** Accessible name, e.g. "Copy the token prefix". */
  label: string;
}

/**
 * Icon-sized copy affordance that flips to a tick for a moment. `CopyableWrapper`
 * cannot be used here: it pins a single DOM id, so several on one screen collide.
 */
const ApiCopyButtonComponent: React.FC<ApiCopyButtonProps> = ({ text, label }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
      }
    },
    []
  );

  const onCopy = useCallback(() => {
    copy(text);
    setCopied(true);
    if (timeout.current !== null) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => setCopied(false), 1600);
  }, [text]);

  const copiedLabel = intl.formatMessage({ id: 'app.copyable_wrapper.copied', defaultMessage: 'Copied' });

  return (
    <button
      type="button"
      className={`${styles.iconButton}${copied ? ` ${styles.iconButtonOk}` : ''}`}
      onClick={onCopy}
      title={copied ? copiedLabel : label}
      aria-label={label}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
    </button>
  );
};

export const ApiCopyButton = TypedMemo(ApiCopyButtonComponent);
